import { Component } from '@theme/component';

/**
 * Slow, hover-pausing Print Finland gallery with glass edge arrows.
 * Native scroll stays available for touchscreens, trackpads, and mouse drags.
 * @extends {Component}
 */
class PrintFinlandConveyor extends Component {
  abortController = null;
  frame = 0;
  lastFrame = null;
  pauseUntil = 0;
  pointerOver = false;
  direction = 1;
  inView = true;
  manuallyPaused = false;
  suppressClicksUntil = 0;

  connectedCallback() {
    super.connectedCallback();
    this.initializeGallery();
  }

  updatedCallback() {
    super.updatedCallback();
    this.initializeGallery();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    cancelAnimationFrame(this.frame);
    super.disconnectedCallback();
  }

  initializeGallery() {
    const { belt, group, previous, next } = this.refs;
    if (!belt || !group || !previous || !next) return;

    this.abortController?.abort();
    cancelAnimationFrame(this.frame);
    this.lastFrame = null;
    this.pointerOver = false;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const hint = this.querySelector('.pfp-conveyor-help');
    if (hint?.id) belt.setAttribute('aria-describedby', hint.id);

    const position = this.querySelector('[data-pf-position]');
    const motion = this.querySelector('[data-pf-motion]');
    const speed = Math.max(10, Math.min(60, Number(this.dataset.speed) || 32));
    this.manuallyPaused = this.dataset.autoplay === 'false';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const hoverCapable = window.matchMedia('(hover: hover)');
    const pause = (ms = 1800) => {
      this.pauseUntil = Math.max(this.pauseUntil, performance.now() + ms);
    };

    const bounds = () => Math.max(0, belt.scrollWidth - belt.clientWidth);
    const updateArrows = () => {
      const max = bounds();
      previous.disabled = max < 2 || belt.scrollLeft < 2;
      next.disabled = max < 2 || belt.scrollLeft >= max - 2;
      if (position) { position.disabled = max < 2; position.value = String(max ? belt.scrollLeft / max * 100 : 0); }
      if (motion) motion.disabled = max < 2;
    };
    updateArrows();

    belt.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.pointerOver = true;
    }, { signal });
    belt.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') {
        this.pointerOver = false;
        pause(500);
      }
    }, { signal });
    this.addEventListener('focusin', () => pause(300), { signal });
    belt.addEventListener('scroll', updateArrows, { passive: true, signal });
    belt.addEventListener('wheel', () => pause(2400), { passive: true, signal });
    this.addEventListener('pointerdown', () => pause(2500), { passive: true, signal });
    this.addEventListener('pointerup', () => pause(2100), { passive: true, signal });
    this.addEventListener('pointercancel', () => pause(2100), { passive: true, signal });

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(updateArrows);
      observer.observe(belt);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    } else {
      window.addEventListener('resize', updateArrows, { signal });
    }

    const step = () => {
      const card = group.querySelector('.pfp-belt-card');
      const gap = parseFloat(getComputedStyle(group).columnGap) || 18;
      return (card?.getBoundingClientRect().width || Math.min(260, belt.clientWidth)) + gap;
    };
    const navigate = (sign) => {
      pause(2500);
      this.direction = sign;
      belt.scrollBy({ left: sign * step(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      updateArrows();
    };
    previous.addEventListener('click', () => navigate(-1), { signal });
    next.addEventListener('click', () => navigate(1), { signal });

    // Drag from product photographs and card backgrounds, not form controls.
    let gesture = null;
    const interactive = 'button, input, select, textarea, label, summary, [contenteditable], [role="button"]';
    belt.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest(interactive)) return;
      gesture = { x: event.clientX, scrollLeft: belt.scrollLeft, moved: false };
    }, { signal });

    const release = () => {
      if (!gesture) return;
      if (gesture.moved) {
        this.suppressClicksUntil = performance.now() + 270;
        pause(2200);
      }
      gesture = null;
      belt.classList.remove('is-dragging');
    };
    window.addEventListener('pointermove', (event) => {
      if (!gesture || event.pointerType !== 'mouse') return;
      if (!(event.buttons & 1)) { release(); return; }
      const distance = event.clientX - gesture.x;
      if (!gesture.moved && Math.abs(distance) < 7) return;
      if (!gesture.moved) {
        gesture.moved = true;
        belt.classList.add('is-dragging');
      }
      event.preventDefault();
      belt.scrollLeft = gesture.scrollLeft - distance;
    }, { signal });
    window.addEventListener('pointerup', release, { signal });
    window.addEventListener('pointercancel', release, { signal });
    belt.addEventListener('click', (event) => {
      if (performance.now() < this.suppressClicksUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { signal, capture: true });
    belt.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    belt.addEventListener('keydown', (event) => {
      if (event.target !== belt) return; // Allow typing and selection in product forms.
      const key = event.key;
      if (key === 'ArrowLeft' || key === 'ArrowRight') {
        event.preventDefault();
        navigate(key === 'ArrowRight' ? 1 : -1);
      } else if (key === 'Home' || key === 'End') {
        event.preventDefault();
        pause(2500);
        belt.scrollTo({ left: key === 'Home' ? 0 : bounds(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    }, { signal });

    // 32px/s, tracked in a fractional offset so sub-pixel frames accumulate.
    // Pause only while the gallery/arrows are hovered or focus is keyboard-visible.
    // Clicking an arrow does not leave auto-play permanently paused.
    let autoPosition = belt.scrollLeft;
    const animate = (now) => {
      const elapsed = this.lastFrame === null ? 0 : Math.min(64, now - this.lastFrame);
      this.lastFrame = now;
      const max = bounds();
      const hovered = this.pointerOver || (hoverCapable.matches &&
        (belt.matches(':hover') || previous.matches(':hover') || next.matches(':hover')));
      const keyboardFocused = this.querySelector(':focus-visible') !== null;
      const paused = this.manuallyPaused || !this.inView || reducedMotion.matches || hovered || keyboardFocused || gesture ||
        now < this.pauseUntil || document.visibilityState === 'hidden';
      if (max > 2 && !paused) {
        if (Math.abs(belt.scrollLeft - autoPosition) > 3) {
          autoPosition = belt.scrollLeft; // Respect touch, wheel and arrow movement.
        }
        let nextPosition = autoPosition + this.direction * speed * elapsed / 1000;
        if (nextPosition >= max) {
          nextPosition = max;
          this.direction = -1;
        } else if (nextPosition <= 0) {
          nextPosition = 0;
          this.direction = 1;
        }
        autoPosition = nextPosition;
        belt.scrollLeft = autoPosition;
      } else {
        autoPosition = belt.scrollLeft;
      }
      this.frame = 0;
      schedule();
    };
    const canAnimate = () => !this.manuallyPaused && this.inView && !reducedMotion.matches && document.visibilityState !== 'hidden';
    const schedule = () => {
      if (!canAnimate()) { cancelAnimationFrame(this.frame); this.frame = 0; this.lastFrame = null; return; }
      if (!this.frame) this.frame = requestAnimationFrame(animate);
    };
    const updateMotion = () => {
      if (motion) {
        motion.hidden = reducedMotion.matches;
        motion.textContent = this.manuallyPaused ? motion.dataset.play : motion.dataset.pause;
        motion.setAttribute('aria-pressed', String(this.manuallyPaused));
      }
      schedule();
    };
    motion?.addEventListener('click', () => { this.manuallyPaused = !this.manuallyPaused; updateMotion(); }, { signal });
    position?.addEventListener('input', () => {
      this.manuallyPaused = true;
      belt.scrollLeft = bounds() * Number(position.value) / 100;
      autoPosition = belt.scrollLeft;
      updateMotion();
      updateArrows();
    }, { signal });
    document.addEventListener('visibilitychange', schedule, { signal });
    reducedMotion.addEventListener('change', updateMotion, { signal });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => { this.inView = entry.isIntersecting; schedule(); });
      observer.observe(this);
      signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
    updateMotion();
  }
}

if (!customElements.get('pf-product-conveyor')) {
  customElements.define('pf-product-conveyor', PrintFinlandConveyor);
}
